import { useRouter } from 'next/router';

import type { GTMEventGlycerinPagination } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { UsePagingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UsePagingFragmentFragmentDoc';

import { decodePage, encodePage, pruneQuery } from '../queryEncoding';

const routePrefixPattern = /^\/c/;
const stripInternalPrefix = (pathname: string) =>
  pathname.replace(routePrefixPattern, '').concat('/');

/* GraphQL */ `
  fragment UsePagingFragment on SearchProductResult {
    summary {
      totalResultCount
    }
  }
`;

/**
 * Use page option persisted in the url query
 *
 * Changing pages results in a router.push, which will trigger a data reload
 */
export const usePaging = (maskedData: FragmentType<typeof UsePagingFragmentFragmentDoc>) => {
  const data = getFragmentData(UsePagingFragmentFragmentDoc, maskedData);

  const dispatchGtmEvent = useTracking();
  const { push, pathname, query } = useRouter();

  const { pageSize, currentPage = 1, pagingMode } = decodePage(query);

  const pageCount = Math.ceil(data.summary.totalResultCount / pageSize);

  /** Strip internal-only prefix and remove undefined query values to get a clean target route */
  const getCleanTargetRoute = (partialQuery: ReturnType<typeof useRouter>['query']) => ({
    // currently the /c/ prefix is internal-only, and rewritten in the middleware
    pathname: stripInternalPrefix(pathname),
    query: pruneQuery({ ...query, ...partialQuery }),
  });

  const dispatchPaginationEvent = (pageNumber: number) => {
    dispatchGtmEvent<GTMEventGlycerinPagination>({
      event: 'Pagination',
      PaginationData: {
        pageNumber,
      },
    });
  };

  const pushPageChange = (newPage: number, keepScrollPosition?: boolean) => {
    if (newPage === currentPage) return;

    const partialQuery = encodePage(newPage !== 1 ? newPage : undefined);
    const targetRoute = getCleanTargetRoute(partialQuery);

    push(targetRoute, undefined, {
      scroll: !keepScrollPosition,
    });

    dispatchPaginationEvent(newPage);
  };

  const pushPagingMode = (newPagingMode: 'limited' | 'infinite') => {
    if (newPagingMode === pagingMode) return;

    const partialQuery = encodePage(newPagingMode === 'infinite' ? 0 : undefined);
    const targetRoute = getCleanTargetRoute(partialQuery);

    push(targetRoute);

    dispatchPaginationEvent(1);
  };

  return {
    pageSize,
    pageCount,
    currentPage,
    pagingMode,
    pushPageChange,
    pushPagingMode,
  };
};
